import { PublicClientApplication } from '@azure/msal-browser';

const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_AZURE_CLIENT_ID || '<YOUR_CLIENT_ID>',
    authority: `https://login.microsoftonline.com/${process.env.REACT_APP_AZURE_TENANT_ID || 'common'}`,
    // Explicit redirect URI from env to avoid mismatches with the Azure App Registration.
    redirectUri: process.env.REACT_APP_AZURE_REDIRECT_URI || window.location.origin,
    navigateToLoginRequestUrl: false
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false
  }
};

export const pca = new PublicClientApplication(msalConfig);

const loginRequest = { scopes: ['openid', 'profile', 'email'] };

// Start initialization immediately at import time. This matters for the popup flow:
// browsers only allow window.open (which loginPopup uses) synchronously inside the click,
// so MSAL must already be initialized by the time the user clicks — otherwise the popup
// gets blocked and opens as a new tab (notably on Safari/mobile).
const initializePromise = pca.initialize();
let msalReady = false;
initializePromise.then(() => { msalReady = true; }).catch(() => { /* surfaced on use */ });

export function ensureMsalInitialized() {
  return initializePromise;
}

// Popups are unreliable on mobile (Microsoft recommends redirect there), and Safari often
// opens them as a tab. Use the full-page redirect flow for those devices.
function preferRedirectFlow() {
  const ua = (navigator && navigator.userAgent) || '';
  return /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
}

// MSAL error codes that mean the popup could not be used — safe to fall back to redirect.
const POPUP_FALLBACK_CODES = [
  'popup_window_error',
  'empty_window_error',
  'block_nested_popups',
  'popup_window_timeout',
  'monitor_window_timeout'
];

/**
 * Start a Microsoft sign-in.
 * - Desktop: opens a popup and returns { idToken } on success.
 * - Mobile / popup blocked: falls back to a full-page redirect and returns { redirecting: true }
 *   (the page navigates away; handleMsalRedirect() completes it when the app reloads).
 * Throws for genuine errors (e.g. user_cancelled) so the caller can show a message.
 */
export function signInWithMicrosoft() {
  const goRedirect = () =>
    ensureMsalInitialized()
      .then(() => pca.loginRedirect(loginRequest))
      .then(() => ({ redirecting: true }));

  if (preferRedirectFlow()) {
    return goRedirect();
  }

  const onPopupResult = (result) => ({ idToken: (result && result.idToken) || null });
  const onPopupError = (err) => {
    const code = (err && err.errorCode) || '';
    if (POPUP_FALLBACK_CODES.includes(code)) {
      // Popup couldn't open / timed out — fall back to redirect.
      return goRedirect();
    }
    throw err;
  };

  // When MSAL is already initialized, call loginPopup synchronously within the click so the
  // browser treats it as user-initiated (prevents it opening as a background tab).
  if (msalReady) {
    return pca.loginPopup(loginRequest).then(onPopupResult).catch(onPopupError);
  }
  return ensureMsalInitialized()
    .then(() => pca.loginPopup(loginRequest))
    .then(onPopupResult)
    .catch(onPopupError);
}

/**
 * Completes a redirect-based sign-in when the app (re)loads. Idempotent and safe to call on
 * every load — returns the Microsoft id_token when returning from a loginRedirect, else null.
 */
let redirectPromise = null;
export function handleMsalRedirect() {
  if (!redirectPromise) {
    redirectPromise = ensureMsalInitialized()
      .then(() => pca.handleRedirectPromise())
      .then((res) => (res && res.idToken ? res.idToken : null))
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.error('MSAL redirect handling failed', e);
        return null;
      });
  }
  return redirectPromise;
}

/**
 * Sign the user out of eHRMS only, leaving their Microsoft browser session alone.
 *
 * `logoutRedirect` normally navigates to Microsoft's end-session endpoint, which
 * drops the shared login cookie for the whole browser — signing the person out of
 * Outlook, Teams and every other Microsoft tab they had open. Leaving eHRMS is not
 * a reason to do that to them.
 *
 * Returning false from `onRedirectNavigate` cancels that navigation while MSAL
 * still clears the tokens it holds for the account, which is the only part of the
 * logout this app has any business doing. The account is read BEFORE the cache is
 * cleared, or there would be nothing left to hand MSAL to clear.
 */
export async function signOutFromMicrosoftLocally() {
  try {
    await ensureMsalInitialized();

    const accounts = pca.getAllAccounts ? pca.getAllAccounts() : [];
    const account = (pca.getActiveAccount && pca.getActiveAccount()) || (accounts.length ? accounts[0] : null);

    const logoutRequest = {
      onRedirectNavigate: () => false
    };
    if (account) logoutRequest.account = account;

    await pca.logoutRedirect(logoutRequest);

    // Belt and braces: MSAL is configured with `cacheLocation: 'sessionStorage'`,
    // so anything it failed to remove above goes here.
    try { sessionStorage.clear(); } catch (e) { /* ignore */ }
    return true;
  } catch (err) {
    // A failed provider-side cleanup must not strand someone in a
    // half-logged-out app: local state is already gone by this point.
    // eslint-disable-next-line no-console
    console.warn('msal local sign out failed', err);
    try { sessionStorage.clear(); } catch (e) { /* ignore */ }
    return false;
  }
}
